<template>
  <div class="geojson-map-slot" @dragover.stop @dragleave.stop @drop.stop.prevent>
    <div class="layer-toolbar border-bottom p-1">
      <label :for="`${mapId}-files`" class="mb-0 mr-1">{{ $t('geoJsonMap.upload') }}</label>
      <input :id="`${mapId}-files`" type="file" accept=".geojson,.json,.zip,application/geo+json,application/json,application/zip"
        multiple :disabled="!mapReady || importing" @change="importFiles" />
      <small class="text-muted d-block mt-50">{{ $t('geoJsonMap.session') }}</small>
      <small class="text-muted d-block">{{ $t('geoJsonMap.shapefileHelp') }}</small>
      <p v-if="importing" class="mb-0 mt-50" role="status">{{ $t('geoJsonMap.importing') }}</p>
      <ul v-if="errors.length" class="mb-0 mt-50 text-danger" role="alert">
        <li v-for="(error, index) in errors" :key="index">{{ error }}</li>
      </ul>
    </div>
    <div class="map-area">
      <div :id="mapId" ref="mapCanvas" class="map-canvas"></div>
      <div v-if="!mapReady" class="map-status p-1" role="status">
        <template v-if="mapFailed">
          <p class="text-danger">{{ $t('geoJsonMap.mapError') }}</p>
          <b-button size="sm" variant="primary" @click="initMap">{{ $t('geoJsonMap.retry') }}</b-button>
        </template>
        <template v-else>{{ $t('geoJsonMap.loadingMap') }}</template>
      </div>
      <aside v-if="mapReady" class="layer-panel p-1 rounded border" :aria-label="$t('geoJsonMap.layers')">
        <h6>{{ $t('geoJsonMap.layers') }}</h6>
        <b-form-checkbox v-model="showUnits" @change="updateUnitVisibility">
          {{ $t('geoJsonMap.facilities') }} ({{ validUnits.length }})
        </b-form-checkbox>
        <small v-if="loadingUnits" class="d-block text-muted" role="status">{{ $t('geoJsonMap.loadingUnits') }}</small>
        <small v-if="missingCoordinates" class="d-block text-muted">
          {{ $t('geoJsonMap.missing', { count: missingCoordinates }) }}
        </small>
        <p v-if="unitsFailed" class="text-danger mb-0" role="alert">{{ $t('geoJsonMap.unitsError') }}</p>
        <b-button size="sm" variant="outline-secondary" class="mt-50" :disabled="!validUnits.length"
          @click="fitUnits">{{ $t('geoJsonMap.locateUnits') }}</b-button>
        <hr class="my-1" />
        <small v-if="labelsLimited" class="d-block text-muted mb-50">{{ $t('geoJsonMap.labelsLimited') }}</small>
        <small v-if="!layers.length" class="text-muted">{{ $t('geoJsonMap.empty') }}</small>
        <div v-for="(layer, index) in layers" :key="layer.id" class="layer-row py-50">
          <div class="d-flex align-items-start">
            <b-form-checkbox v-model="layer.visible" class="flex-grow-1 layer-name" @change="updateLayers">
              {{ layer.name }}
            </b-form-checkbox>
            <input v-model="layer.color" type="color" class="layer-color ml-50"
              :aria-label="$t('geoJsonMap.color', { name: layer.name })" @input="updateLayers" />
          </div>
          <div class="d-flex align-items-center mt-25">
            <small class="text-muted mr-auto">{{ $t('geoJsonMap.matches', { count: layer.matchCount, total: layer.count }) }}</small>
            <b-button size="sm" variant="flat-secondary" :disabled="index === 0"
              :aria-label="$t('geoJsonMap.raise')" @click="moveLayer(index, -1)">↑</b-button>
            <b-button size="sm" variant="flat-secondary" :disabled="index === layers.length - 1"
              :aria-label="$t('geoJsonMap.lower')" @click="moveLayer(index, 1)">↓</b-button>
            <b-button size="sm" variant="flat-secondary" :aria-label="$t('geoJsonMap.locate')"
              @click="fitLayer(layer)"><feather-icon icon="CrosshairIcon" /></b-button>
            <b-button size="sm" variant="flat-danger" :aria-label="$t('geoJsonMap.remove')"
              @click="removeLayer(layer.id)"><feather-icon icon="Trash2Icon" /></b-button>
          </div>
          <div v-if="layer.numericFields.length" class="mt-50">
            <label :for="`${layer.id}-metric`">{{ $t('geoJsonMap.metric') }}</label>
            <select :id="`${layer.id}-metric`" v-model="layer.metric" class="form-control form-control-sm" @change="updateLayers">
              <option value="">{{ $t('geoJsonMap.noMetric') }}</option>
              <option v-for="field in layer.numericFields" :key="field" :value="field">{{ field }}</option>
            </select>
          </div>
          <details v-if="layer.fields.length" class="mt-50">
            <summary>{{ $t('geoJsonMap.filters') }}</summary>
            <div v-for="(filter, filterIndex) in layer.filters" :key="filter.id" class="mt-50">
              <div class="d-flex align-items-center justify-content-between">
                <label :for="filter.id" class="mb-0">{{ $t('geoJsonMap.filterLabel', { number: filterIndex + 1 }) }}</label>
                <b-button size="sm" variant="flat-danger" :aria-label="$t('geoJsonMap.removeFilter')"
                  @click="removeFilter(layer, filter.id)"><feather-icon icon="Trash2Icon" /></b-button>
              </div>
              <select :id="filter.id" v-model="filter.field" class="form-control form-control-sm"
                @change="setFilterField(layer, filter)">
                <option value="">{{ $t('geoJsonMap.noFilter') }}</option>
                <option v-for="field in layer.fields" :key="field" :value="field">{{ field }}</option>
              </select>
              <template v-if="filter.field">
                <div class="d-flex my-25">
                  <b-button size="sm" variant="flat-secondary" @click="selectFilterValues(layer, filter, true)">{{ $t('geoJsonMap.all') }}</b-button>
                  <b-button size="sm" variant="flat-secondary" @click="selectFilterValues(layer, filter, false)">{{ $t('geoJsonMap.none') }}</b-button>
                </div>
                <input v-model="filter.search" type="search" class="form-control form-control-sm mb-50"
                  :placeholder="$t('geoJsonMap.search')" :aria-label="$t('geoJsonMap.search')" />
                <div class="filter-values">
                  <b-form-checkbox v-for="option in filterOptions(filter)" :key="option.key"
                    v-model="filter.values" :value="option.key" @change="updateLayers">
                    {{ option.label == null ? $t('geoJsonMap.noValue') : option.label }} ({{ option.count }})
                  </b-form-checkbox>
                </div>
                <small class="text-muted">{{ $t('geoJsonMap.selected', { count: filter.values.length, total: filter.options.length }) }}</small>
              </template>
            </div>
            <b-button size="sm" variant="outline-primary" class="mt-50" @click="addFilter(layer)">
              {{ $t('geoJsonMap.addFilter') }}
            </b-button>
          </details>
        </div>
      </aside>
    </div>
  </div>
</template>

<script>
import { Map as OMS } from '@omnicloud/maps-sdk';
import { BButton, BFormCheckbox } from 'bootstrap-vue';
import { v4 as uuidv4 } from 'uuid';
import useAppConfig from '@core/app-config/useAppConfig';
import store from '@/store';
import layoutMixin from '@/mixins/layoutMixin';
import { SLOT_TYPES } from '@/config/layoutConfig';
import { getCspNonce } from '@/utils/cspNonce';
import { readMapFile, attributeFields, attributeKey, featureMatches, numericFields, numericValue } from './geojson';

const COLORS = ['#3388ff', '#e67e22', '#8e44ad', '#16a085', '#e74c3c', '#d4ac0d'];

export default {
  components: { BButton, BFormCheckbox },
  mixins: [layoutMixin],
  i18n: {
    messages: {
      es: { geoJsonMap: {
        upload: 'Cargar GeoJSON / ZIP', session: 'Hasta 100 MB por archivo. Las capas duran solo durante esta sesión del widget.',
        shapefileHelp: 'Shapefile: comprime juntos .shp, .dbf y .prj del mismo nombre en un ZIP. Puedes incluir los demás archivos auxiliares.',
        layers: 'Capas (superiores primero)', facilities: 'Instalaciones', loadingUnits: 'Cargando instalaciones…',
        loadingMap: 'Cargando mapa…', importing: 'Cargando archivos…', empty: 'Carga GeoJSON o un ZIP con Shapefiles para agregar capas.',
        missing: '{count} instalaciones sin coordenadas válidas.', features: '{count} elementos', color: 'Color de {name}',
        raise: 'Subir capa', lower: 'Bajar capa', locate: 'Ubicar capa', remove: 'Eliminar capa', locateUnits: 'Ubicar instalaciones',
        mapError: 'No se pudo cargar el mapa.', unitsError: 'No se pudieron actualizar las instalaciones.', retry: 'Reintentar',
        invalidGeoJson: 'GeoJSON inválido. Usa coordenadas WGS84 [longitud, latitud] y geometrías válidas.',
        emptyGeoJson: 'El archivo no contiene geometrías con ubicación.', tooLarge: 'El archivo supera los 100 MB.',
        importError: 'No se pudo leer o dibujar el archivo.',
        invalidShapefile: 'No se pudo convertir el ZIP. Verifica que incluya .shp, .dbf y .prj compatibles y que no tenga contraseña.',
        unsupportedFile: 'Carga GeoJSON o comprime los archivos del Shapefile juntos en un ZIP.',
        filters: 'Filtros', filterLabel: 'Filtro {number}', addFilter: 'Agregar filtro', removeFilter: 'Eliminar filtro', noFilter: 'Selecciona un campo',
        metric: 'Dato a mostrar sobre las zonas', noMetric: 'Sin etiquetas', details: 'Entidad seleccionada',
        labelsLimited: 'Se muestran hasta 200 etiquetas en la zona visible. Acerca o filtra el mapa para ver otras.',
        all: 'Todos', none: 'Ninguno', search: 'Buscar valores', noValue: 'Sin dato',
        selected: '{count} de {total} valores seleccionados (hasta 200 visibles; usa la búsqueda).', matches: 'Coinciden {count} / {total}',
      } },
      en: { geoJsonMap: {
        upload: 'Load GeoJSON / ZIP', session: 'Up to 100 MB per file. Layers last only for this widget session.',
        shapefileHelp: 'Shapefile: zip matching .shp, .dbf and .prj files together. Other companion files can be included.',
        layers: 'Layers (top first)', facilities: 'Facilities', loadingUnits: 'Loading facilities…',
        loadingMap: 'Loading map…', importing: 'Loading files…', empty: 'Load GeoJSON or a ZIP containing Shapefiles to add layers.',
        missing: '{count} facilities without valid coordinates.', features: '{count} features', color: 'Color of {name}',
        raise: 'Raise layer', lower: 'Lower layer', locate: 'Locate layer', remove: 'Remove layer', locateUnits: 'Locate facilities',
        mapError: 'Could not load the map.', unitsError: 'Could not update facilities.', retry: 'Retry',
        invalidGeoJson: 'Invalid GeoJSON. Use WGS84 [longitude, latitude] coordinates and valid geometries.',
        emptyGeoJson: 'The file contains no geometries with a location.', tooLarge: 'The file exceeds 100 MB.',
        importError: 'Could not read or draw the file.',
        invalidShapefile: 'Could not convert the ZIP. Check that it contains matching .shp, .dbf and .prj files and is not password protected.',
        unsupportedFile: 'Load GeoJSON or zip the Shapefile companion files together.',
        filters: 'Filters', filterLabel: 'Filter {number}', addFilter: 'Add filter', removeFilter: 'Remove filter', noFilter: 'Select a field',
        metric: 'Value to display over areas', noMetric: 'No labels', details: 'Selected feature',
        labelsLimited: 'Up to 200 labels are shown in the visible area. Zoom in or filter the map to see others.',
        all: 'All', none: 'None', search: 'Search values', noValue: 'No value',
        selected: '{count} of {total} values selected (up to 200 displayed; use search).', matches: '{count} / {total} matching',
      } },
      pt: { geoJsonMap: {
        upload: 'Carregar GeoJSON / ZIP', session: 'Até 100 MB por arquivo. As camadas duram apenas durante esta sessão do widget.',
        shapefileHelp: 'Shapefile: compacte juntos .shp, .dbf e .prj de mesmo nome em um ZIP. Outros arquivos auxiliares podem ser incluídos.',
        layers: 'Camadas (superiores primeiro)', facilities: 'Instalações', loadingUnits: 'Carregando instalações…',
        loadingMap: 'Carregando mapa…', importing: 'Carregando arquivos…', empty: 'Carregue GeoJSON ou um ZIP com Shapefiles para adicionar camadas.',
        missing: '{count} instalações sem coordenadas válidas.', features: '{count} elementos', color: 'Cor de {name}',
        raise: 'Subir camada', lower: 'Descer camada', locate: 'Localizar camada', remove: 'Remover camada', locateUnits: 'Localizar instalações',
        mapError: 'Não foi possível carregar o mapa.', unitsError: 'Não foi possível atualizar as instalações.', retry: 'Tentar novamente',
        invalidGeoJson: 'GeoJSON inválido. Use coordenadas WGS84 [longitude, latitude] e geometrias válidas.',
        emptyGeoJson: 'O arquivo não contém geometrias com localização.', tooLarge: 'O arquivo excede 100 MB.',
        importError: 'Não foi possível ler ou desenhar o arquivo.',
        invalidShapefile: 'Não foi possível converter o ZIP. Verifique se contém .shp, .dbf e .prj compatíveis e não possui senha.',
        unsupportedFile: 'Carregue GeoJSON ou compacte os arquivos do Shapefile juntos em um ZIP.',
        filters: 'Filtros', filterLabel: 'Filtro {number}', addFilter: 'Adicionar filtro', removeFilter: 'Remover filtro', noFilter: 'Selecione um campo',
        metric: 'Dado a mostrar sobre as áreas', noMetric: 'Sem etiquetas', details: 'Entidade selecionada',
        labelsLimited: 'São mostradas até 200 etiquetas na área visível. Aproxime ou filtre o mapa para ver outras.',
        all: 'Todos', none: 'Nenhum', search: 'Buscar valores', noValue: 'Sem dado',
        selected: '{count} de {total} valores selecionados (até 200 visíveis; use a busca).', matches: 'Correspondem {count} / {total}',
      } },
    },
  },
  data() {
    return {
      mapId: `geojson-map-${uuidv4()}`,
      mapReady: false,
      mapFailed: false,
      unitsFailed: false,
      importing: false,
      showUnits: true,
      labelsLimited: false,
      layers: [],
      errors: [],
    };
  },
  computed: {
    units() { return store.state.grid.units; },
    loadingUnits() { return store.state.grid.loadingUnits; },
    unitsVersion() { return store.state.grid.unitsVersion; },
    validUnits() {
      return this.units.reduce((result, unit) => {
        const coordinates = [unit.coordinates, { lat: unit.router?.lat, lng: unit.router?.lng ?? unit.router?.lon }]
          .find(value => value && ['lat', 'lng'].every(key => ['number', 'string'].includes(typeof value[key])
            && String(value[key]).trim() !== '' && Number.isFinite(Number(value[key])))
            && Math.abs(Number(value.lat)) <= 90 && Math.abs(Number(value.lng)) <= 180);
        if (coordinates) result.push({ ...unit, coordinates: { lat: Number(coordinates.lat), lng: Number(coordinates.lng) } });
        return result;
      }, []);
    },
    missingCoordinates() { return this.units.length - this.validUnits.length; },
  },
  watch: {
    validUnits() { this.renderUnits(); },
    unitsVersion() { this.renderUnits(); },
    loadingUnits(value) { if (!value) this.renderUnits(); },
    '$i18n.locale'(locale) {
      if (this.sdk) this.sdk.setLocale(locale);
      this.closeFeatureDetails();
      this.refreshLabels();
    },
  },
  mounted() {
    this.mapLayers = new Map();
    this.renderedUnitIds = new Set();
    this.labels = new Map();
    this.featurePositions = new WeakMap();
    this.layerListeners = new Map();
    this.$refs.mapCanvas.addEventListener('camera:click', this.onCameraClick);
    this.$refs.mapCanvas.addEventListener('camera:click-float', this.onCameraClickFloat);
    this.$refs.mapCanvas.addEventListener('plane:click', this.onPlaneClick);
    this.$refs.mapCanvas.addEventListener('marker:click', this.closeFeatureDetails);
    this.initMap();
  },
  beforeDestroy() {
    this.disposed = true;
    clearTimeout(this.readyTimer);
    if (this.cancelReady) this.cancelReady();
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.labelsIdleListener) this.labelsIdleListener.remove();
    this.layerListeners.forEach(listener => listener.remove());
    this.layerListeners.clear();
    this.labels.forEach(({ marker }) => { marker.map = null; });
    this.labels.clear();
    this.closeFeatureDetails();
    if (this.featureInfoWindow) google.maps.event.clearInstanceListeners(this.featureInfoWindow);
    this.$refs.mapCanvas.removeEventListener('camera:click', this.onCameraClick);
    this.$refs.mapCanvas.removeEventListener('camera:click-float', this.onCameraClickFloat);
    this.$refs.mapCanvas.removeEventListener('plane:click', this.onPlaneClick);
    this.$refs.mapCanvas.removeEventListener('marker:click', this.closeFeatureDetails);
    this.mapLayers.forEach(layer => layer.setMap(null));
    this.mapLayers.clear();
    if (this.sdk) this.sdk.destroy();
  },
  methods: {
    async initMap() {
      if (this.initializing || this.disposed) return;
      this.initializing = true;
      this.mapFailed = false;
      const { skin } = useAppConfig();
      const sdk = new OMS({ token: window.localStorage.accessToken, apiUrl: `${window.__env.VUE_APP_BASE_URL}/v1`, locale: this.$i18n.locale });
      this.sdk = sdk;
      try {
        const ready = sdk.onMapReady();
        ready.then(() => { if (this.disposed || this.sdk !== sdk) sdk.destroy(); });
        const timeout = new Promise((resolve, reject) => {
          this.cancelReady = () => reject(new Error('disposed'));
          this.readyTimer = setTimeout(() => reject(new Error('timeout')), 30000);
        });
        const configured = sdk.setConfig({
          container: `#${this.mapId}`, latLng: { lat: 19.39068, lng: -99.2836975 }, zoom: 5,
          gmapsKey: window.__env.VUE_APP_GMAPS_KEY, triggerEvents: true, locale: this.$i18n.locale,
          darkMode: skin.value === 'dark', mapId: skin.value === 'dark' ? 'eac5c4d4f71b58d6' : 'SBO', nonce: getCspNonce(),
        });
        await Promise.race([Promise.all([configured, ready]), timeout]);
        if (this.disposed) return;
        this.mapReady = true;
        this.labelsIdleListener = sdk.map.map.addListener('idle', this.refreshLabels);
        this.renderUnits();
        this.resizeObserver = new ResizeObserver(() => {
          if (this.mapReady && !this.disposed) google.maps.event.trigger(sdk.map.map, 'resize');
        });
        this.resizeObserver.observe(this.$refs.mapCanvas);
      } catch (error) {
        if (!this.disposed) {
          this.mapFailed = true;
          this.mapReady = false;
          if (this.sdk === sdk) this.sdk = null;
          sdk.destroy();
        }
      } finally {
        clearTimeout(this.readyTimer);
        this.cancelReady = null;
        this.initializing = false;
      }
    },
    async renderUnits() {
      if (!this.mapReady || this.disposed) return;
      try {
        const ids = new Set(this.validUnits.map(unit => String(unit.id)));
        if (Array.from(this.renderedUnitIds).some(id => !ids.has(id)) && this.sdk.map.unitLayer) {
          this.sdk.map.unitLayer.destroy();
          this.sdk.map.unitLayer = null;
        }
        await this.sdk.setMarkers(this.validUnits);
        if (this.disposed) return;
        this.renderedUnitIds = ids;
        this.unitsFailed = false;
        this.updateUnitVisibility();
        if (!this.hasFramed && this.validUnits.length && !this.loadingUnits) this.fitUnits();
      } catch (error) { this.unitsFailed = true; }
    },
    updateUnitVisibility() {
      if (!this.mapReady || this.disposed) return;
      if (this.showUnits) this.sdk.map.showMarkers();
      else {
        this.sdk.map.hideMarkers();
        this.sdk.map.infowindow?.close();
      }
    },
    fitBounds(bounds) {
      if (bounds.isEmpty()) return;
      this.sdk.map.map.fitBounds(bounds, 40);
      if (this.sdk.map.map.getZoom() > 18) this.sdk.map.map.setZoom(18);
      this.hasFramed = true;
    },
    fitUnits() {
      this.showUnits = true;
      this.updateUnitVisibility();
      const bounds = new google.maps.LatLngBounds();
      this.validUnits.forEach(unit => bounds.extend(unit.coordinates));
      this.fitBounds(bounds);
    },
    async importFiles(event) {
      const files = Array.from(event.target.files || []);
      event.target.value = '';
      if (this.importing || !this.mapReady || this.disposed) return;
      this.importing = true;
      this.errors = [];
      const bounds = new google.maps.LatLngBounds();
      for (const file of files) {
        const addedIds = [];
        const pendingLayers = [];
        try {
          const entries = await readMapFile(file);
          if (this.disposed) break;
          entries.forEach(({ name, collection }) => {
            const dataLayer = new google.maps.Data();
            pendingLayers.push(dataLayer);
            const features = dataLayer.addGeoJson(collection);
            const layer = {
              id: uuidv4(), name, visible: true, color: COLORS[this.layers.length % COLORS.length], count: features.length,
              matchCount: features.length, fields: attributeFields(collection),
              numericFields: numericFields(collection), metric: '',
              filters: [],
            };
            addedIds.push(layer.id);
            this.mapLayers.set(layer.id, dataLayer);
            this.layerListeners.set(layer.id, dataLayer.addListener('click', event => this.showFeatureDetails(layer, event.feature, event.latLng)));
            this.layers.unshift(layer);
          });
          this.updateLayers();
          pendingLayers.forEach(dataLayer => dataLayer.forEach(feature => feature.getGeometry()?.forEachLatLng(position => bounds.extend(position))));
        } catch (error) {
          pendingLayers.forEach(layer => layer.setMap(null));
          addedIds.forEach(id => this.mapLayers.delete(id));
          addedIds.forEach(id => {
            this.layerListeners.get(id)?.remove();
            this.layerListeners.delete(id);
          });
          this.layers = this.layers.filter(layer => !addedIds.includes(layer.id));
          if (!this.disposed) {
            const key = ['tooLarge', 'invalidGeoJson', 'emptyGeoJson', 'invalidShapefile', 'unsupportedFile'].includes(error.message) ? error.message : 'importError';
            this.errors.push(`${file.name}: ${this.$t(`geoJsonMap.${key}`)}`);
          }
        }
      }
      if (!this.disposed) {
        this.importing = false;
        this.fitBounds(bounds);
      }
    },
    updateLayers() {
      this.layers.forEach((layer, index) => {
        const native = this.mapLayers.get(layer.id);
        const filters = this.activeFilters(layer);
        let count = 0;
        native.forEach(feature => { if (featureMatches(feature, filters)) count += 1; });
        layer.matchCount = count;
        const style = { fillColor: layer.color, fillOpacity: 0.25, strokeColor: layer.color,
          strokeWeight: 2, zIndex: this.layers.length - index, clickable: true,
          icon: { path: google.maps.SymbolPath.CIRCLE, scale: 5, fillColor: layer.color, fillOpacity: 1, strokeColor: layer.color, strokeWeight: 1 },
        };
        native.setStyle(feature => ({ ...style, visible: featureMatches(feature, filters) }));
        native.setMap(layer.visible ? this.sdk.map.map : null);
      });
      this.closeFeatureDetails();
      this.refreshLabels();
    },
    featurePosition(feature) {
      if (!this.featurePositions.has(feature)) {
        const bounds = new google.maps.LatLngBounds();
        feature.getGeometry()?.forEachLatLng(position => bounds.extend(position));
        this.featurePositions.set(feature, bounds.isEmpty() ? null : bounds.getCenter());
      }
      return this.featurePositions.get(feature);
    },
    refreshLabels() {
      if (!this.mapReady || this.disposed) return;
      const viewport = this.sdk.map.map.getBounds();
      if (!viewport) return;
      const wanted = new Set();
      let limited = false;
      const formatter = new Intl.NumberFormat(this.$i18n.locale);
      this.layers.forEach((layer, index) => {
        if (!layer.visible || !layer.metric) return;
        const filters = this.activeFilters(layer);
        this.mapLayers.get(layer.id).forEach(feature => {
          if (!featureMatches(feature, filters)) return;
          const value = numericValue(feature.getProperty(layer.metric));
          if (value === null) return;
          const position = this.featurePosition(feature);
          if (!position || !viewport.contains(position)) return;
          if (wanted.size >= 200) { limited = true; return; }
          wanted.add(feature);
          let label = this.labels.get(feature);
          if (!label) {
            const button = document.createElement('button');
            button.type = 'button';
            button.style.cssText = 'background:#fff;color:#222;border:2px solid;border-radius:4px;padding:2px 5px;font:bold 12px sans-serif;cursor:pointer;white-space:nowrap;box-shadow:0 1px 3px #777';
            const marker = new google.maps.marker.AdvancedMarkerElement({ map: this.sdk.map.map, position, anchorLeft: '-50%', anchorTop: '-50%' });
            marker.replaceChildren(button);
            button.addEventListener('click', () => this.showFeatureDetails(layer, feature, position));
            label = { marker, button };
            this.labels.set(feature, label);
          }
          label.button.textContent = formatter.format(value);
          label.button.style.borderColor = layer.color;
          label.button.title = `${layer.name} · ${layer.metric}: ${formatter.format(value)}`;
          label.marker.zIndex = this.layers.length - index;
        });
      });
      this.labels.forEach(({ marker }, feature) => {
        if (!wanted.has(feature)) { marker.map = null; this.labels.delete(feature); }
      });
      this.labelsLimited = limited;
    },
    closeFeatureDetails() {
      if (this.featureInfoWindow) this.featureInfoWindow.close();
    },
    showFeatureDetails(layer, feature, position) {
      if (this.disposed || !layer.visible || !featureMatches(feature, this.activeFilters(layer))) return;
      const content = document.createElement('div');
      content.style.cssText = 'max-height:320px;max-width:360px;overflow:auto;color:#222;background:#fff;padding:8px';
      const title = document.createElement('strong');
      title.textContent = `${this.$t('geoJsonMap.details')} · ${layer.name}`;
      content.appendChild(title);
      const table = document.createElement('table');
      table.style.cssText = 'width:100%;margin-top:8px;border-collapse:collapse';
      feature.forEachProperty((value, field) => {
        const row = document.createElement('tr');
        const heading = document.createElement('th');
        heading.textContent = field;
        heading.style.cssText = 'text-align:left;padding:5px;border-bottom:1px solid #ddd;vertical-align:top';
        const cell = document.createElement('td');
        cell.textContent = value == null ? this.$t('geoJsonMap.noValue') : typeof value === 'object' ? JSON.stringify(value) : String(value);
        cell.style.cssText = 'padding:5px;border-bottom:1px solid #ddd;overflow-wrap:anywhere';
        if (field === layer.metric) row.style.backgroundColor = '#fff2c6';
        row.appendChild(heading);
        row.appendChild(cell);
        table.appendChild(row);
      });
      content.appendChild(table);
      if (!this.featureInfoWindow) this.featureInfoWindow = new google.maps.InfoWindow();
      this.sdk.map.infowindow?.close();
      this.featureInfoWindow.setContent(content);
      this.featureInfoWindow.setPosition(position || this.featurePosition(feature));
      this.featureInfoWindow.open({ map: this.sdk.map.map, shouldFocus: false });
    },
    activeFilters(layer) {
      return layer.filters.map(filter => ({ field: filter.field, values: new Set(filter.values) }));
    },
    addFilter(layer) {
      layer.filters.push({ id: uuidv4(), field: '', values: [], options: [], search: '' });
    },
    removeFilter(layer, id) {
      layer.filters = layer.filters.filter(filter => filter.id !== id);
      this.updateLayers();
    },
    setFilterField(layer, filter) {
      const options = new Map();
      if (filter.field) {
        this.mapLayers.get(layer.id).forEach(feature => {
          const value = feature.getProperty(filter.field);
          const key = attributeKey(value);
          const option = options.get(key);
          if (option) option.count += 1;
          else options.set(key, { key, label: value == null ? null : String(value), count: 1 });
        });
      }
      filter.options = Array.from(options.values()).sort((a, b) => String(a.label ?? '').localeCompare(String(b.label ?? ''), undefined, { numeric: true }));
      filter.values = filter.options.map(option => option.key);
      filter.search = '';
      this.updateLayers();
    },
    selectFilterValues(layer, filter, selectAll) {
      filter.values = selectAll ? filter.options.map(option => option.key) : [];
      this.updateLayers();
    },
    filterOptions(filter) {
      const query = filter.search.trim().toLocaleLowerCase();
      return filter.options.filter(option => String(option.label ?? this.$t('geoJsonMap.noValue')).toLocaleLowerCase().includes(query)).slice(0, 200);
    },
    fitLayer(layer) {
      layer.visible = true;
      this.updateLayers();
      const bounds = new google.maps.LatLngBounds();
      const filters = this.activeFilters(layer);
      this.mapLayers.get(layer.id).forEach(feature => {
        if (featureMatches(feature, filters)) feature.getGeometry()?.forEachLatLng(position => bounds.extend(position));
      });
      this.fitBounds(bounds);
    },
    moveLayer(index, direction) {
      const target = index + direction;
      if (target < 0 || target >= this.layers.length) return;
      const [layer] = this.layers.splice(index, 1);
      this.layers.splice(target, 0, layer);
      this.updateLayers();
    },
    removeLayer(id) {
      this.mapLayers.get(id).setMap(null);
      this.mapLayers.delete(id);
      this.layerListeners.get(id)?.remove();
      this.layerListeners.delete(id);
      this.layers = this.layers.filter(layer => layer.id !== id);
      this.updateLayers();
    },
    onCameraClick({ detail }) {
      const camera = this.units.find(unit => unit.id === detail.unit_id)?.cameras?.find(item => item.id === detail.camera_id);
      if (camera) this.setSlotData(SLOT_TYPES.CAM_LIVE, camera.name, { camera });
    },
    onCameraClickFloat({ detail }) {
      const camera = this.units.find(unit => unit.id === detail.unit_id)?.cameras?.find(item => item.id === detail.camera_id);
      if (camera) store.commit('grid/addFloatVideos', { id: camera.camera_id, camera_proxy_id: camera.camera_proxy_id, type: camera.camera_type, uuid: uuidv4() });
    },
    onPlaneClick({ detail }) {
      const unit = this.units.find(item => item.id === detail.unit_id);
      if (unit) this.setSlotData(SLOT_TYPES.PLANE, `plane_${unit.id}`, { planes_url: unit.unit_planes });
    },
  },
};
</script>

<style scoped>
.geojson-map-slot { display: flex; flex-direction: column; height: 100%; min-height: 0; }
.layer-toolbar { flex-shrink: 0; max-height: 35%; overflow: auto; }
.layer-toolbar input { max-width: 100%; }
.map-area { position: relative; flex: 1; min-height: 180px; }
.map-canvas { width: 100%; height: 100%; position: absolute; inset: 0; }
.map-status { position: absolute; top: 15px; left: 15px; background: #fff; color: #333; }
.layer-panel { position: absolute; bottom: 20px; left: 10px; width: 290px; max-width: calc(100% - 75px); max-height: 65%; overflow: auto; background: #fff; }
.dark-layout .layer-panel { background: #283046; }
.layer-name { overflow-wrap: anywhere; min-width: 0; }
.layer-color { width: 28px; height: 24px; padding: 0; border: 0; flex-shrink: 0; }
.layer-row + .layer-row { border-top: 1px solid #ddd; }
.filter-values { max-height: 160px; overflow: auto; }
</style>
